import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { fetchFacets } from "@/lib/searchClient";
import { adminApiConfigured, adminQuery, adminSearch, adminStats } from "@/lib/adminClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertTriangle, ChevronLeft, ChevronRight, Loader2, Play, RefreshCw, ShieldCheck } from "lucide-react";

const PAGE_SIZE = 50;
const ANY = "__any";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

function fmtBytes(n) {
  if (n == null) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = Number(n);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function Notice({ title, children, testid }) {
  return (
    <div className="mx-auto max-w-md rounded-lg border border-border p-6 text-center" data-testid={testid}>
      <div className="font-display text-lg tracking-tight">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function ErrorBox({ children }) {
  return (
    <div
      className="whitespace-pre-wrap break-words rounded-md border border-destructive/40 bg-destructive/5 p-3 font-mono-bd text-xs text-destructive"
      data-testid="admin-error"
    >
      {children}
    </div>
  );
}

/** One cell of arbitrary ClickHouse output. */
function Cell({ value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-foreground/60">—</span>;
  }
  const text = typeof value === "object" ? JSON.stringify(value) : String(value);
  return (
    <span className="block max-w-[22rem] truncate" title={text}>
      {text}
    </span>
  );
}

// ── Authenticator-code gate ──────────────────────────────────────────────────
//
// Every admin request needs an aal2 session, which Supabase only issues after
// an authenticator-app code. First visit: scan a QR code once (enrol). Every
// new login after that: type the current 6-digit code (verify). Nothing else
// in the app asks for it — only this tab.

function MfaGate({ children }) {
  const [stage, setStage] = useState("checking"); // checking | enroll | verify | ok | error
  const [factorId, setFactorId] = useState("");
  const [qr, setQr] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // StrictMode runs effects twice in development. Two enrolments racing each
  // other leave the second failing on a duplicate factor name.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      const { data: aal, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aalErr) { setError(aalErr.message); setStage("error"); return; }
      if (aal.currentLevel === "aal2") { setStage("ok"); return; }

      const { data: factors, error: listErr } = await supabase.auth.mfa.listFactors();
      if (listErr) { setError(listErr.message); setStage("error"); return; }

      const verified = factors.totp[0];
      if (verified) {
        setFactorId(verified.id);
        setStage("verify");
        return;
      }

      // An abandoned enrolment leaves an unverified factor behind, and it
      // blocks a new one with the same name. Clear those out first.
      for (const f of factors.all) {
        if (f.factor_type === "totp" && f.status !== "verified") {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
      }

      const { data: enrolled, error: enrollErr } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "InboundLabs admin",
        issuer: "InboundLabs",
      });
      if (enrollErr) { setError(enrollErr.message); setStage("error"); return; }
      setFactorId(enrolled.id);
      setQr(enrolled.totp.qr_code);
      setSecret(enrolled.totp.secret);
      setStage("enroll");
    })();
  }, []);

  const submit = async (value) => {
    const entered = value ?? code;
    if (entered.length !== 6 || busy) return;
    setBusy(true);
    setError("");
    const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: entered });
    setBusy(false);
    if (verifyErr) {
      setCode("");
      setError("That code didn't match. Codes change every 30 seconds — enter the one showing now.");
      return;
    }
    setStage("ok");
  };

  if (stage === "ok") return children;

  if (stage === "checking") {
    return (
      <div className="mx-auto max-w-md space-y-3">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div className="mx-auto max-w-md space-y-3">
        <Notice title="Couldn't start two-factor sign-in" testid="admin-mfa-error">
          Two-factor authentication may be switched off for this Supabase project. In the dashboard, open
          Authentication → Multi-Factor and enable Authenticator app (TOTP).
        </Notice>
        <ErrorBox>{error}</ErrorBox>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md space-y-5 rounded-lg border border-border p-6" data-testid={`admin-mfa-${stage}`}>
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-primary" />
        <div className="font-display text-lg tracking-tight">
          {stage === "enroll" ? "Set up your authenticator" : "Enter your authenticator code"}
        </div>
      </div>

      {stage === "enroll" ? (
        <>
          <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            <li>Open an authenticator app — Google Authenticator, Microsoft Authenticator, 1Password or Authy.</li>
            <li>Add an account and scan this code.</li>
            <li>Type the 6-digit code it shows.</li>
          </ol>
          <div className="flex justify-center rounded-md bg-white p-3">
            <img src={qr} alt="Authenticator QR code" className="h-44 w-44" data-testid="admin-mfa-qr" />
          </div>
          <div className="text-xs text-muted-foreground">
            Can't scan? Enter this key instead:
            <div className="mt-1 select-all break-all rounded border border-border bg-surface px-2 py-1 font-mono-bd text-foreground">
              {secret}
            </div>
          </div>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Open your authenticator app and type the current code for InboundLabs. You'll need it once per login.
        </p>
      )}

      <div className="flex flex-col items-center gap-3">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={setCode}
          onComplete={submit}
          disabled={busy}
          autoFocus
          data-testid="admin-mfa-code"
        >
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
          </InputOTPGroup>
        </InputOTP>
        <Button onClick={() => submit()} disabled={busy || code.length !== 6} className="w-full" data-testid="admin-mfa-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
        </Button>
        {error && <p className="text-center text-sm text-destructive">{error}</p>}
      </div>
    </div>
  );
}

// ── Browse ───────────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  name: "",
  email: "",
  title: "",
  company_name: "",
  company_domain: "",
  country: "",
  industry: ANY,
};

const BROWSE_COLUMNS = [
  ["full_name", "Name"],
  ["title", "Title"],
  ["company_name", "Company"],
  ["email", "Email"],
  ["mobile_phone", "Mobile"],
  ["direct_phone", "Direct"],
  ["company_phone", "Company phone"],
  ["seniority", "Seniority"],
  ["company_industry", "Industry"],
  ["company_size", "Size"],
  ["person_location", "Location"],
  ["company_domain", "Domain"],
  ["linkedin_url", "LinkedIn"],
  ["source", "Source"],
  ["loaded_at", "Loaded"],
  ["id", "person_id"],
];

const TEXT_FILTERS = [
  ["name", "Name", "Jane Smith"],
  ["email", "Email (exact)", "jane@acme.com"],
  ["title", "Title", "VP Sales"],
  ["company_name", "Company", "Acme"],
  ["company_domain", "Domain", "acme.com"],
  ["country", "Country (exact)", "United States"],
];

function BrowseTab() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [industries, setIndustries] = useState([]);
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchFacets().then((f) => setIndustries(f?.industry || []));
  }, []);

  const run = useCallback(async (nextPage, f) => {
    setLoading(true);
    setError("");
    const payload = { limit: PAGE_SIZE, offset: nextPage * PAGE_SIZE };
    for (const [key, value] of Object.entries(f)) {
      if (key === "industry") continue;
      if (value.trim()) payload[key] = value.trim();
    }
    if (f.industry !== ANY) payload.industry = [f.industry];
    try {
      setResult(await adminSearch(payload));
      setPage(nextPage);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { run(0, EMPTY_FILTERS); }, [run]);

  const onSubmit = (e) => { e.preventDefault(); run(0, filters); };
  const onReset = () => { setFilters(EMPTY_FILTERS); run(0, EMPTY_FILTERS); };
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const rows = result?.rows || [];
  const from = rows.length ? page * PAGE_SIZE + 1 : 0;
  const to = page * PAGE_SIZE + rows.length;
  const hasNext = result && (result.total_is_capped || to < result.total);

  return (
    <div className="space-y-4">
      <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-border p-4" data-testid="admin-browse-form">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TEXT_FILTERS.map(([key, label, placeholder]) => (
            <div key={key}>
              <Label htmlFor={`f-${key}`} className="text-xs">{label}</Label>
              <Input
                id={`f-${key}`}
                value={filters[key]}
                onChange={set(key)}
                placeholder={placeholder}
                className="mt-1 h-8"
                data-testid={`admin-filter-${key}`}
              />
            </div>
          ))}
          <div>
            <Label className="text-xs">Industry</Label>
            <Select value={filters.industry} onValueChange={(v) => setFilters((f) => ({ ...f, industry: v }))}>
              <SelectTrigger className="mt-1 h-8" data-testid="admin-filter-industry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Any industry</SelectItem>
                {industries.map((i) => (
                  <SelectItem key={i.value} value={i.value}>
                    {i.value} · {fmt(i.count)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="submit" size="sm" disabled={loading} data-testid="admin-browse-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onReset} disabled={loading}>
            Reset
          </Button>
        </div>
      </form>

      {error && <ErrorBox>{error}</ErrorBox>}

      {result && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span data-testid="admin-browse-count">
            {rows.length
              ? `${fmt(from)}–${fmt(to)} of ${fmt(result.total)}${result.total_is_capped ? "+" : ""}`
              : "No matches"}
          </span>
          <div className="flex gap-1">
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={loading || page === 0}
              onClick={() => run(page - 1, filters)} aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="outline" className="h-8 w-8" disabled={loading || !hasNext}
              onClick={() => run(page + 1, filters)} aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border" data-testid="admin-browse-table">
          <Table className="text-xs">
            <TableHeader>
              <TableRow>
                {BROWSE_COLUMNS.map(([key, label]) => (
                  <TableHead key={key} className="whitespace-nowrap">{label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  {BROWSE_COLUMNS.map(([key]) => (
                    <TableCell key={key} className="whitespace-nowrap py-1.5">
                      <Cell value={r[key]} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ── SQL ──────────────────────────────────────────────────────────────────────

const EXAMPLES = [
  ["Sample rows", "SELECT *\nFROM people\nLIMIT 50"],
  ["Rows by source", "SELECT source, count() AS rows\nFROM people\nGROUP BY source\nORDER BY rows DESC"],
  ["Rows by industry", "SELECT company_industry, count() AS rows\nFROM people\nGROUP BY company_industry\nORDER BY rows DESC"],
  ["Rows by load date", "SELECT toDate(loaded_at) AS day, count() AS rows\nFROM people\nGROUP BY day\nORDER BY day"],
  ["Columns", "DESCRIBE TABLE people"],
  ["Tables", "SHOW TABLES"],
];

function SqlTab() {
  const [sql, setSql] = useState(EXAMPLES[0][1]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    if (!sql.trim() || loading) return;
    setLoading(true);
    setError("");
    try {
      setResult(await adminQuery(sql));
    } catch (e) {
      setResult(null);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      run();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1.5">
        {EXAMPLES.map(([label, text]) => (
          <Button key={label} size="sm" variant="outline" className="h-7 text-xs" onClick={() => setSql(text)}>
            {label}
          </Button>
        ))}
      </div>

      <Textarea
        value={sql}
        onChange={(e) => setSql(e.target.value)}
        onKeyDown={onKeyDown}
        spellCheck={false}
        rows={8}
        className="font-mono-bd text-sm"
        data-testid="admin-sql-input"
      />

      <div className="flex items-center gap-3">
        <Button onClick={run} disabled={loading || !sql.trim()} className="gap-2" data-testid="admin-sql-run">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run
        </Button>
        <span className="text-xs text-muted-foreground">
          ⌘+Enter · read-only · stops after 25 s · first {fmt(1000)} rows
        </span>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {result && (
        <>
          <div className="text-xs text-muted-foreground" data-testid="admin-sql-meta">
            {fmt(result.rows.length)} {result.rows.length === 1 ? "row" : "rows"} · {fmt(result.elapsed_ms)} ms
            {result.truncated && " · stopped at the 1,000-row limit — add a LIMIT or narrow the query"}
          </div>
          {result.columns.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-border" data-testid="admin-sql-table">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    {result.columns.map((c) => (
                      <TableHead key={c} className="whitespace-nowrap font-mono-bd">{c}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.rows.map((r, i) => (
                    <TableRow key={i}>
                      {r.map((v, j) => (
                        <TableCell key={j} className="whitespace-nowrap py-1.5">
                          <Cell value={v} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Stats ────────────────────────────────────────────────────────────────────

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-2xl tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function SmallTable({ title, rows, labelKey, labelHeader }) {
  return (
    <div className="rounded-lg border border-border">
      <div className="border-b border-border px-4 py-2 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
        {title}
      </div>
      <Table className="text-sm">
        <TableHeader>
          <TableRow>
            <TableHead>{labelHeader}</TableHead>
            <TableHead className="text-right">Rows</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r[labelKey]}>
              <TableCell>{r[labelKey] || <span className="text-muted-foreground">(blank)</span>}</TableCell>
              <TableCell className="text-right tabular-nums">{fmt(r.rows)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deepLoading, setDeepLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (deep) => {
    const setBusy = deep ? setDeepLoading : setLoading;
    setBusy(true);
    setError("");
    try {
      setStats(await adminStats(deep));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  if (!stats) {
    return error ? <ErrorBox>{error}</ErrorBox> : (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  const distinct = stats.distinct_people_estimate;
  const extra = distinct != null ? stats.total_rows - distinct : null;
  // uniqCombined is an estimate; below ~1% of the table the gap is noise.
  const duplicated = extra != null && extra > stats.total_rows * 0.01;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="gap-2" onClick={() => load(false)} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button size="sm" className="gap-2" onClick={() => load(true)} disabled={deepLoading} data-testid="admin-stats-deep">
          {deepLoading && <Loader2 className="h-4 w-4 animate-spin" />} Check for duplicates
        </Button>
        <span className="text-xs text-muted-foreground">Reads every person_id — takes a few seconds.</span>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3" data-testid="admin-stats-cards">
        <Stat label="Rows" value={fmt(stats.total_rows)} hint="count() — includes unmerged copies" />
        <Stat
          label="Distinct people"
          value={distinct != null ? `≈ ${fmt(distinct)}` : "—"}
          hint={distinct != null ? "estimate, within about 1%" : "run “Check for duplicates”"}
        />
        <Stat label="Size on disk" value={fmtBytes(stats.bytes_on_disk)} />
        <Stat label="Active parts" value={fmt(stats.active_parts)} hint="many parts = merges still pending" />
        <Stat label="Pending mutations" value={fmt(stats.pending_mutations)} hint="should be 0" />
        <Stat label="Email lookup rows" value={fmt(stats.email_lookup_rows)} hint="people_by_email" />
      </div>

      {distinct != null && (
        <div
          className={`rounded-lg border p-4 text-sm ${duplicated ? "border-amber-500/50 bg-amber-500/5" : "border-border"}`}
          data-testid="admin-stats-verdict"
        >
          {duplicated ? (
            <>
              <span className="font-medium">About {fmt(extra)} rows look like extra copies of the same people.</span>{" "}
              That is {((extra / stats.total_rows) * 100).toFixed(1)}% of the table. Search may show some people twice.
            </>
          ) : (
            <>
              <span className="font-medium">No meaningful duplication.</span> The row count is real, distinct people.
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <SmallTable title="Rows by source" rows={stats.by_source} labelKey="source" labelHeader="Source" />
        <SmallTable title="Rows by load date" rows={stats.by_load_date} labelKey="date" labelHeader="Loaded" />
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

// The Catalog tab of the Admin page. Admin.jsx already turns away non-admins;
// this adds the authenticator-code step the catalog endpoints require.
export default function AdminCatalog() {
  if (!adminApiConfigured()) {
    return (
      <Notice title="Catalog API not configured" testid="admin-no-api">
        REACT_APP_SEARCH_API_URL is not set for this build, so there is no catalog to administer.
      </Notice>
    );
  }

  return (
    <MfaGate>
      <div className="space-y-6" data-testid="admin-workspace">
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span>
            Raw emails and phone numbers — the values customers pay to reveal. Don't screen-share this page.
            Every search and query here is logged with your account.
          </span>
        </div>

        <Tabs defaultValue="browse">
          <TabsList>
            <TabsTrigger value="browse" data-testid="admin-tab-browse">Browse</TabsTrigger>
            <TabsTrigger value="sql" data-testid="admin-tab-sql">SQL</TabsTrigger>
            <TabsTrigger value="stats" data-testid="admin-tab-stats">Stats</TabsTrigger>
          </TabsList>
          {/* forceMount keeps each tab's results when you switch away and back. */}
          <TabsContent value="browse" forceMount className="mt-6 data-[state=inactive]:hidden"><BrowseTab /></TabsContent>
          <TabsContent value="sql" forceMount className="mt-6 data-[state=inactive]:hidden"><SqlTab /></TabsContent>
          <TabsContent value="stats" forceMount className="mt-6 data-[state=inactive]:hidden"><StatsTab /></TabsContent>
        </Tabs>
      </div>
    </MfaGate>
  );
}
